<template>
    <div>
        <div class="mission-header">
            <h4 class="header-title">
                {{ mission.mission.name }}
                <small class="finished-date">Afgerond op {{ mission.finished_at | date_named_month_short }}</small>
            </h4>
            <b-badge
                class="mission-status-badge"
                v-if="missionStatusText !== null"
                pill
                :variant="getMissionStatus()"
                style="padding: 10px"
                >{{ missionStatusText }}
            </b-badge>
            <b-button
                class="btn-clean btn-reopen"
                variant="primary-icon-only"
                @click="unlockMission(mission.id)">
            </b-button>
        </div>

        <b-card
            class="mission-rating-card quest-mission-review-card margin-bottom"
            header-tag="div"
            no-body>
            <b-card-header header-class="quest-mission-review-card-header">
                <h4 class="title">{{ $t('quest.rating') }}</h4>
            </b-card-header>
            <b-card-body body-class="quest-mission-review-card-content">
                <div class="rating">
                    <div class="rating-input form-group">
                        <label for="missionrating">1. Cijfer (1 t/m 10)</label>
                        <input
                            class="form-control"
                            id="missionrating"
                            v-model="mission.teacher"
                            type="text"
                            :placeholder="$t('quest.grade')" />
                    </div>
                    <div
                        class="rating-grade"
                        v-if="mission.status == 3">
                        <p class="label">{{ $t('quest.total_points') }}</p>
                        <design-2021-quest-result-progress-bar-component
                            :usermission="mission"></design-2021-quest-result-progress-bar-component>
                    </div>
                    <div
                        class="rating-not-graded"
                        v-else>
                        <p class="h5">{{ $t('quest.not_rated_yet') }}</p>
                    </div>
                </div>

                <div class="options">
                    <div class="difficulty">
                        <p class="label">{{ $t('quest.difficulty') }}</p>
                        <p>{{ mission.mission.difficulty }}</p>
                    </div>

                    <div class="time-percentage">
                        <p class="label">Timer percentage</p>
                        <design-2021-quest-deadline-progress-component
                            :deadline="mission.deadline"></design-2021-quest-deadline-progress-component>
                    </div>

                    <div class="bonus form-group">
                        <label>Bonus</label>
                        <input
                            class="form-control"
                            v-model="mission.bonus"
                            type="text"
                            placeholder="Bonus" />
                    </div>
                </div>

                <div class="feedback form-group">
                    <label>Feedback</label>
                    <textarea
                        class="form-control apprentice-textarea"
                        v-model="mission.comment"
                        rows="4"></textarea>
                </div>

                <div class="badges">
                    <h6>Badges</h6>
                    <p class="info-text">
                        {{
                            $t(
                                'quest.give_a_badge_as_an_extra_reward_for_example_in_case_the_student_does_has_done_something_special',
                            )
                        }}
                    </p>
                    <div class="new-badge">
                        <b-button
                            class="btn-clean btn-new-badge"
                            v-b-modal="'mission-user-badge-modal'"
                            variant="primary">
                            {{ $t('quest.award_new_badge') }}
                        </b-button>
                    </div>

                    <div
                        class="userbadge"
                        v-for="badge in mission.badges"
                        :key="badge.id">
                        <img :src="badge.image" />

                        <b-button
                            class="btn-clean"
                            variant="inverse-danger"
                            @click="deleteBadge(badge.id)">
                            <i class="fa fa-trash"></i>
                        </b-button>
                    </div>
                </div>

                <div class="rating-save">
                    <b-button
                        class="btn-clean btn-rating-grade"
                        variant="primary"
                        @click="saveMission()">
                        {{ $t('common.save') }}
                    </b-button>
                </div>
            </b-card-body>
        </b-card>

        <b-card
            class="mission-hand-in-card quest-mission-review-card margin-bottom"
            header-tag="div"
            no-body>
            <b-card-header header-class="quest-mission-review-card-header">
                <h4 class="title">{{ $t('quest.submitted_work') }}</h4>
            </b-card-header>
            <b-card-body body-class="quest-mission-review-card-content">
                <div
                    class="videos"
                    v-if="videos.length > 0">
                    <h6>Video{{ videos.length > 1 ? "'s" : '' }}</h6>
                    <div
                        class="video"
                        v-for="video in videos"
                        :key="video.id">
                        <design-2021-quest-mission-user-review-video-component :video="video">
                        </design-2021-quest-mission-user-review-video-component>
                    </div>
                </div>

                <div
                    class="images-list"
                    id="quest-user-vids"
                    v-if="images.length > 0">
                    <h6>{{ $t('quest.image') }}{{ images.length > 1 ? 'en' : '' }}</h6>
                    <div
                        class="images clearfix"
                        v-viewer="viewerOptions">
                        <template v-for="image in images">
                            <div
                                class="image-holder"
                                :key="image.id">
                                <img
                                    class="image"
                                    :src="image.path"
                                    :data-source="image.path"
                                    :key="image.path"
                                    :alt="image.name" />
                            </div>
                        </template>
                    </div>
                </div>

                <div
                    class="regular-files"
                    v-if="otherFiles.length > 0">
                    <h6>Bestand{{ otherFiles.length > 1 ? 'en' : '' }}</h6>
                    <b-list-group>
                        <b-list-group-item
                            class="regular-file"
                            v-for="file in otherFiles"
                            :key="file.id">
                            <b-button
                                class="btn-clean btn-file-download btn-inverse-primary"
                                variant="link"
                                target="_blank"
                                :href="file.path">
                                <i class="fa fa-download"></i><span class="file-name">{{ file.name }}</span></b-button
                            >
                        </b-list-group-item>
                    </b-list-group>
                </div>

                <div
                    class="links"
                    v-if="mission.links.length > 0">
                    <h6>Linkje{{ mission.links.length > 1 ? 's' : '' }}</h6>

                    <b-list-group>
                        <b-list-group-item
                            class="link-item"
                            v-for="link in mission.links"
                            :key="link.id">
                            <div class="icon btn-external"></div>
                            <div class="link-title">{{ link.name }}</div>
                            <a
                                class="clickable"
                                :href="link.url"
                                target="_blank"></a>
                        </b-list-group-item>
                    </b-list-group>
                </div>

                <p
                    class="info-text"
                    v-if="
                        mission.links.length == 0 && otherFiles.length == 0 && images.length == 0 && videos.length == 0
                    ">
                    {{ $t('quest.no_files_submitted') }}
                </p>
            </b-card-body>
        </b-card>

        <b-card
            class="mission-assignment-card quest-mission-review-card margin-bottom"
            header-tag="div"
            no-body>
            <b-card-header header-class="quest-mission-review-card-header">
                <h4 :class="title">{{ $t('quest.assignment') }}</h4>
            </b-card-header>
            <b-card-body
                class="quest-mission-review-card-content"
                v-html="mission.mission.description">
            </b-card-body>
        </b-card>
    </div>
</template>

<script>
export default {
    name: 'design-2021-quest-mission-user-review-component',
    props: {
        mission: Object,
        badges: Array,
        userId: Number,
        questId: Number,
    },
    data() {
        return {
            viewerOptions: {
                inline: false,
                button: true,
                navbar: true,
                title: true,
                toolbar: true,
                tooltip: true,
                movable: true,
                zoomable: true,
                rotatable: true,
                scalable: false,
                transition: true,
                fullscreen: true,
                keyboard: false,
                url: 'data-source',
            },
            usermission: this.mission,
            missionStatusText: null,
            files: this.mission.files,
            videos: this.filterVideoFiles(this.mission.files),
            images: this.filterImageFiles(this.mission.files),
            otherFiles: this.filterOtherFiles(this.mission.files),
        };
    },
    methods: {
        getVideoSource(file) {
            return [
                {
                        type: 'video/mp4',
                    src: file.path,
                },
            ];
        },

        unlockMission(mission_id) {
            this.$emit('callunlockmission', mission_id);
        },

        saveMission() {
            this.$emit(
                'callsavemission',
                this.mission.id,
                this.mission.teacher,
                this.mission.bonus,
                this.mission.comment,
            );
        },

        deleteBadge(id) {
            this.$emit('calldeletebadge', id);
        },

        addBadge(id) {
            this.$emit('calladdbadge', id);
        },
        getMissionStatus() {
            if (this.mission.status === 1) {
                this.missionStatusText = 'Open';
                return 'secondary';
            }
            if (this.mission.status === 2) {
                this.missionStatusText = this.$t('quest.submitted');
                return 'warning';
            }
            if (this.mission.status === 3) {
                this.missionStatusText = this.$t('quest.reviewed');
                return 'success';
            }
        },
        filterVideoFiles(files) {
            if (!files) {
                return [];
            }
            return files.filter((file) => {
                const extensions = ['mp4', 'MP4', 'mov', 'MOV'];
                for (const ext of extensions) {
                    if (ext === file.extension) {
                        return true;
                    }
                }
                return false;
            });
        },
        filterImageFiles(files) {
            if (!files) {
                return [];
            }
            return files.filter((file) => {
                const extensions = [
                    'jpg',
                    'JPG',
                    'jpeg',
                    'JPEG',
                    'png',
                    'PNG',
                    'gif',
                    'GIF',
                    'tiff',
                    'TIFF',
                    'bmp',
                    'BMP',
                    'jif',
                    'jfif',
                ];
                for (const ext of extensions) {
                    if (ext === file.extension) {
                        return true;
                    }
                }
                return false;
            });
        },
        filterOtherFiles(files) {
            if (!files) {
                return [];
            }
            return files.filter((file) => {
                const extensions = [
                    'jpg',
                    'JPG',
                    'jpeg',
                    'JPEG',
                    'png',
                    'PNG',
                    'gif',
                    'GIF',
                    'tiff',
                    'TIFF',
                    'bmp',
                    'BMP',
                    'mp4',
                    'mov',
                    'MOV',
                    'MP4',
                    'jif',
                    'jfif',
                ];
                for (const ext of extensions) {
                    if (ext === file.extension) {
                        return false;
                    }
                }
                return true;
            });
        },
    },
    mounted() {
        if (this.mission.status === 1) {
            this.missionStatusText = 'Open';
        }

        if (this.mission.status === 2) {
            this.missionStatusText = this.$t('quest.submitted');
        }

        if (this.mission.status === 3) {
            this.missionStatusText = this.$t('quest.reviewed');
        }
    },
};
</script>

<style scoped lang="scss">
.quest-mission-review-card {
    padding: 0;
    border: 0;

    .quest-mission-review-card-header {
        background-color: $info;
        color: $white;
        padding: map-get($grid-gutter-spacing, 'fifteen') map-get($grid-gutter-spacing, 'twenty-one');
        border-top-left-radius: $border-radius-content;
        border-top-right-radius: $border-radius-content;

        .title {
            margin-bottom: 0;

            i {
                margin-right: map-get($grid-gutter-spacing, 'ten');
            }
        }
    }

    .quest-mission-review-card-content {
        padding: map-get($grid-gutter-spacing, 'fiften') map-get($grid-gutter-spacing, 'twenty-one')
            map-get($grid-gutter-spacing, 'twenty-one') map-get($grid-gutter-spacing, 'twenty-one');
        border-left: 1px solid $border-color;
        border-right: 1px solid $border-color;
        border-bottom: 1px solid $border-color;
        border-bottom-right-radius: $border-radius-content;
        border-bottom-left-radius: $border-radius-content;

        .info-text {
            color: $text-muted-color;
        }
    }
}

.mission-status-badge {
    align-self: center;
    margin-left: auto;
    margin-right: 15px;
}

.mission-header {
    display: flex;
    justify-content: space-between;
    align-content: center;
    margin-bottom: 30px;
}

.mission-header .header-title {
    display: flex;
    flex-direction: column;
}

.header-title .finished-date {
    color: #686868;
    font-size: 0.875rem;
    margin-top: 3px;
}

.header-title .btn-reopen {
    flex: 0 1;
    margin-left: 15px;
    align-self: baseline;
}

.mission-rating-card .card-body {
    padding: 15px 20px;
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
}

.mission-rating-card label,
.mission-rating-card .label {
    font-weight: 500;
    margin-bottom: 5px;
}

.rating label {
    font-weight: 500;
}

.rating-not-graded,
.difficulty,
.time-percentage {
    margin-bottom: 24px;
}

.time-percentage ::v-deep {
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;

    .label {
        display: flex;
        flex: 1 0 100%;
    }
}

.rating-save {
    text-align: right;
}

.badges {
    text-align: left;
    margin-bottom: 30px;
}

.image-holder {
    height: 150px;
    width: 100%;
    margin-bottom: map-get($grid-gutter-spacing, 'fifteen');

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
    }

    &:hover {
        cursor: pointer;
    }
}

.userbadge {
    float: left;
    text-align: center;
}

.userbadge img {
    height: 100px;
    width: 100px;
    margin: 10px;
}

.videos,
.images-list,
.regular-files {
    margin-bottom: 30px;

    &:not(:last-child) {
        border-bottom: 3px dotted $border-color;
    }
}

.videos {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;

    h6 {
        flex: 1 0 100%;
    }

    .video {
        width: 100%;
    }
}

.video {
    margin-bottom: 15px;
}

.regular-file {
    padding: 5px 5px 5px 0;
    border: none;
    display: flex;
    justify-content: flex-start;
    align-content: center;
}

.link-item {
    position: relative;
    display: flex;
    justify-content: flex-start;
    align-content: center;
    font-size: $base-font-size;
    border: 0;
    border-radius: $border-radius-content;
    padding: map-get($grid-gutter-spacing, 'eight') map-get($grid-gutter-spacing, 'twenty-one')
        map-get($grid-gutter-spacing, 'eight') 0;

    .icon {
        align-self: flex-start;
        padding: map-get($grid-gutter-spacing, 'ten');
        background: $secondary-background-color;
        border-radius: $border-radius-content;
            color: var(--primary);

        &:before {
            margin-right: 0;
        }
    }

    .link-title {
        font-size: $default-font-size;
        align-self: center;
        margin-left: map-get($grid-gutter-spacing, 'fifteen');
        color: $text-muted-color;
        font-weight: $font-weight-medium;
    }

    &:hover {
        background-color: $secondary-background-color;
        border-radius: $border-radius-content;
    }
}

.regular-file:not(:first-child),
.link-item:not(:first-child) {
    border-top: 1px solid #eee;
}

.btn-download {
    margin-top: 15px;
}

.btn-download i,
.btn-link-address i {
    margin-right: 15px;
}

.btn-link-address,
.btn-download,
.btn-file-download,
.btn-link-address:hover,
.btn-file-download:hover,
.btn-download:hover {
    text-decoration: none;
}

.regular-file {
    align-content: center;
}

.file-name {
    margin-left: 10px;
    margin-right: 30px;
}

.file-name,
.regular-file i {
    align-self: center;
}

@media (min-width: map-get($grid-breakpoints, 'sm')) {
    .videos {
        justify-content: space-between;

        .video {
            width: calc(50% - 15px);
        }
    }

    .images {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        flex-wrap: wrap;
        gap: 0 15px;

        .image-holder {
            width: calc(50% - 15px);
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .mission-rating-card .card-body {
        flex-direction: row;
        flex-wrap: wrap;
    }

    .rating,
    .options,
    .feedback,
    .badges {
        display: flex;
        flex: 1 0 100%;
        justify-content: space-between;
        margin-bottom: 15px;
    }

    .rating > div {
        flex-basis: 45%;
    }

    .options > div {
        flex-basis: 33%;
    }

    .feedback,
    .badges {
        flex-direction: column;
    }

    .badges .new-badge {
        flex: 0 1 auto;
        justify-content: flex-end;
        display: flex;
    }
}

@media (min-width: map-get($grid-breakpoints, 'xl')) {
    .videos {
        justify-content: flex-start;
        gap: 0 15px;

        .video {
            width: calc(33.3% - 15px);
        }
    }

    .images {
        .image-holder {
            width: calc(33.3% - 15px);
        }
    }
}
</style>

<style>
.viewer-zoom-in::before,
.viewer-zoom-out::before,
.viewer-one-to-one::before,
.viewer-reset::before,
.viewer-prev::before,
.viewer-play::before,
.viewer-next::before,
.viewer-rotate-left::before,
.viewer-rotate-right::before,
.viewer-flip-horizontal::before,
.viewer-flip-vertical::before,
.viewer-fullscreen::before,
.viewer-fullscreen-exit::before,
.viewer-close::before {
    position: absolute;
}
</style>
